import * as React from 'react'

import type { DraggableProps } from 'react-draggable'

// Re-export the type surface so consumers keep a single import site. These are
// `import type` / `export type` only — they are erased at build time and create
// NO runtime dependency on react-draggable.
export type {
  ControlPosition,
  DraggableBounds,
  DraggableCoreProps,
  DraggableData,
  DraggableEvent,
  DraggableProps,
  PositionOffsetControlPosition,
} from 'react-draggable'

// Lazy on purpose — keep react-draggable out of the eager Module Federation
// bootstrap chunk. react-draggable is a CommonJS lib whose Draggable.js /
// DraggableCore.js dereference react-dom at module-eval time
// (`_interopRequireDefault(require('react-dom'))`). When it lands in the eager,
// modulepreloaded chunk it reads MF's async-shared react-dom binding before it
// is initialised and crashes the whole app at boot with
// `ReferenceError: $xx is not defined` (blank screen). The only durable fix is
// to guarantee react-draggable is reached solely through a dynamic import, so
// it is emitted into a lazy chunk that loads after the shared react-dom
// singleton is ready. react-draggable is pulled in through many, shifting
// static paths (map view, calendar, action bars, tables), so we cut it here at
// the single library boundary rather than chasing every consumer view.
//
// Do NOT convert this back to a static `import ... from 'react-draggable'`.
const LazyDraggableImpl = React.lazy(() => import('react-draggable'))

/**
 * Drop-in replacement for react-draggable's default `Draggable` export.
 *
 * While the react-draggable chunk is loading, the children render as-is
 * (non-draggable) via the Suspense fallback, so nothing disappears; the moment
 * the chunk resolves they are re-rendered inside the real `<Draggable>`. After
 * the first use anywhere the chunk is cached, so this is effectively instant.
 */
// react-draggable's `Draggable` is typed as `React.Component<Partial<DraggableProps>>`,
// so every prop is optional at the call site — mirror that here.
const Draggable: React.FC<Partial<DraggableProps>> = ({
  children,
  ...rest
}) => (
  <React.Suspense fallback={<>{children}</>}>
    <LazyDraggableImpl {...rest}>{children}</LazyDraggableImpl>
  </React.Suspense>
)

export default Draggable
